import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// Ritaglio: avatar con MASCHERA CIRCOLARE (esterno oscurato, anteprima = risultato finale,
// output tecnico 1:1) oppure copertina panoramica 3:1 rettangolare.
// Pointer Events = mouse e touch. Esc/Enter da tastiera. Nessun salvataggio prima della conferma.
// allowContain (loghi strutture): zoom-out con margine di sicurezza su sfondo bianco, senza tagliare il logo.
export function ImageCropModal({ kind, onUpload, onClose, initialBlob = null, allowContain = false }) {
  const { t } = useTranslation();
  const isAvatar = kind === "avatar";
  const RATIO = isAvatar ? 1 : 3;
  const OUT_W = isAvatar ? 512 : 1500;
  const OUT_H = Math.round(OUT_W / RATIO);
  const FRAME_W = isAvatar ? 280 : Math.min(560, window.innerWidth - 64);
  const FRAME_H = Math.round(FRAME_W / RATIO);
  const [src, setSrc] = useState(null); // { img, cover, contain }
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const drag = useRef(null);
  const rootRef = useRef(null);

  const loadImg = (blob) => {
    const img = new Image();
    img.onload = () => {
      const cover = Math.max(FRAME_W / img.width, FRAME_H / img.height);
      const contain = Math.min(FRAME_W / img.width, FRAME_H / img.height) * 0.88; // margine di sicurezza logo
      setSrc({ img, cover, contain });
      setZoom(allowContain ? contain / cover : 1);
      setOff({ x: 0, y: 0 });
    };
    img.onerror = () => toast.error(t("photo.upload_err"));
    img.src = URL.createObjectURL(blob);
  };
  const pick = (e) => { const f = e.target.files[0]; if (f) loadImg(f); e.target.value = ""; };
  useEffect(() => { if (initialBlob) loadImg(initialBlob); }, [initialBlob]);
  useEffect(() => { rootRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
      if (e.key === "Enter" && src && !busy) confirmCrop();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const disp = () => src ? { w: src.img.width * src.cover * zoom, h: src.img.height * src.cover * zoom } : { w: 0, h: 0 };
  const clamp = (o, z = zoom) => {
    if (!src) return o;
    const { w, h } = (() => { const s = src.cover * z; return { w: src.img.width * s, h: src.img.height * s }; })();
    const xMin = Math.min(0, FRAME_W - w), xMax = Math.max(0, FRAME_W - w);
    const yMin = Math.min(0, FRAME_H - h), yMax = Math.max(0, FRAME_H - h);
    return { x: Math.min(xMax, Math.max(xMin, o.x)), y: Math.min(yMax, Math.max(yMin, o.y)) };
  };
  const covers = src && disp().w >= FRAME_W && disp().h >= FRAME_H;
  const canConfirm = !!src && !busy && (allowContain || covers);
  const down = (e) => { drag.current = { x: e.clientX - off.x, y: e.clientY - off.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (drag.current) setOff(clamp({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y })); };
  const up = () => { drag.current = null; };
  const confirmCrop = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUT_W; canvas.height = OUT_H;
      const ctx = canvas.getContext("2d");
      const s = OUT_W / FRAME_W;
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT_W, OUT_H);
      const { w, h } = disp();
      ctx.drawImage(src.img, off.x * s, off.y * s, w * s, h * s);
      const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", 0.9));
      await onUpload(blob);
      onClose();
    } catch { toast.error(t("photo.upload_err")); }
    finally { setBusy(false); }
  };
  const minZoom = src ? (allowContain ? src.contain / src.cover : 1) : 1;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid={`${kind}-crop-modal`}>
      <div ref={rootRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={t("photo.crop")}
           className="yp-card p-4 w-full max-w-xl outline-none" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold mb-3">{t("photo.crop")} · {t("photo.reposition")}</h3>
        {!src ? (
          <label className="yp-btn yp-btn-outline cursor-pointer" data-testid="crop-choose">
            {t("photo.choose")}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="crop-file-input" />
          </label>
        ) : (<>
          <div className="mx-auto relative touch-none select-none"
               style={{ width: FRAME_W, height: FRAME_H }}
               onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="crop-frame">
            <div className={`w-full h-full overflow-hidden bg-white ${isAvatar ? "rounded-full" : "rounded"}`}>
              <img src={src.img.src} alt="" draggable={false}
                   style={{ width: disp().w, height: disp().h, transform: `translate(${off.x}px, ${off.y}px)`, maxWidth: "none" }} />
            </div>
            {isAvatar && (
              <div className="absolute inset-0 rounded-full pointer-events-none"
                   style={{ boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)" }}
                   data-testid="crop-circle-mask" />)}
          </div>
          <label className="block text-xs text-slate-500 mt-3">{t("photo.zoom")}
            <input type="range" min={minZoom} max="3" step="0.01" value={zoom}
                   onChange={e => { const z = Number(e.target.value); setZoom(z); setOff(o => clamp(o, z)); }}
                   className="w-full" data-testid="crop-zoom" aria-label={t("photo.zoom")} />
          </label>
        </>)}
        <div className="flex justify-end gap-2 mt-4">
          <button className="yp-btn yp-btn-ghost" onClick={onClose} disabled={busy} data-testid="crop-cancel">{t("common.cancel")}</button>
          <button className="yp-btn yp-btn-primary" onClick={confirmCrop} disabled={!canConfirm} data-testid="crop-confirm">
            {busy ? t("photo.uploading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>);
}
